import { useState, useEffect } from 'react';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PlusCircle, Pencil, Trash2, Shield } from 'lucide-react';

const ROLES = [
  { value: 'equipe_interne', label: 'Equipe interne EE' },
  { value: 'collaborateur_pde', label: 'Collaborateur PDE' },
];

const ROLE_COLORS = {
  equipe_interne: '#397DFF',
  collaborateur_pde: '#F59E0B'
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'equipe_interne' });
  const [editingId, setEditingId] = useState(null);

  const isAdmin = user?.role === 'equipe_interne';

  const fetchUsers = async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      const res = await API.get('/auth/users');
      setUsers(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [isAdmin]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', email: '', password: '', role: 'equipe_interne' });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingId(u.user_id);
    setForm({ name: u.name, email: u.email, password: '', role: u.role });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await API.put(`/auth/users/${editingId}`, payload);
        toast.success('Utilisateur mis a jour');
      } else {
        await API.post('/auth/register', form);
        toast.success('Utilisateur cree');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await API.delete(`/auth/users/${uid}`);
      toast.success('Utilisateur supprime');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-4 fade-in" data-testid="settings-page">
        <h1 className="text-2xl font-heading font-bold">Parametres</h1>
        <Card className="border"><CardContent className="py-8 text-center text-sm text-muted-foreground">
          Acces reserve aux administrateurs
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-in" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0B1120]">Parametres</h1>
          <p className="text-sm text-muted-foreground">Gestion des utilisateurs</p>
        </div>
        <Button className="bg-[#397DFF] hover:bg-[#2563EB] text-white gap-1" size="sm"
          onClick={openCreate} data-testid="add-user-btn">
          <PlusCircle size={14} /> Nouvel utilisateur
        </Button>
      </div>

      <Card className="border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold">Nom</TableHead>
              <TableHead className="text-xs font-semibold">Email</TableHead>
              <TableHead className="text-xs font-semibold">Role</TableHead>
              <TableHead className="text-xs font-semibold w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8">Chargement...</TableCell></TableRow>
            ) : users.map(u => (
              <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                <TableCell className="text-sm font-medium">{u.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <Badge className="text-[10px]" style={{ backgroundColor: ROLE_COLORS[u.role] || '#94A3B8', color: '#fff' }}>
                    {ROLES.find(r => r.value === u.role)?.label || u.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(u)}
                      data-testid={`edit-user-${u.user_id}`}>
                      <Pencil size={13} />
                    </Button>
                    {u.user_id !== user?.user_id && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive"
                        onClick={() => handleDelete(u.user_id)} data-testid={`delete-user-${u.user_id}`}>
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? 'Modifier' : 'Nouvel'} utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Nom</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="h-9 text-sm" data-testid="user-form-name" />
            </div>
            {!editingId && (
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="h-9 text-sm" data-testid="user-form-email" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">{editingId ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe'}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                className="h-9 text-sm" data-testid="user-form-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-[#397DFF] hover:bg-[#2563EB] text-white" onClick={handleSubmit}
              data-testid="user-form-submit">
              {editingId ? 'Mettre a jour' : 'Creer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
